<template>
	<div>
		<div class="flex mb-4">
			<p>
				{{ setName ? capitalize(setName) : "" }}:
				{{ selectedSize?.Name || selectedSize?.Code }}
			</p>
		</div>
		<RadioGroup :id="`${selectedSize?.ParametricCode}`" v-model="selectedSize">
			<RadioGroupLabel
				:id="`label-${selectedSize?.ParametricCode}`"
				class="sr-only"
			>
				{{ setName ? capitalize(setName) : "" }}
				{{ selectedSize?.Name || selectedSize?.Code }}
			</RadioGroupLabel>
			<div class="flex flex-wrap gap-1.5 max-w-full">
				<RadioGroupOption
					v-for="size in sizes"
					:id="`${size?.ParametricCode}-${size?.ParametricId}-${size?.Id}`"
					:key="size?.Id"
					v-slot="{ checked }"
					as="template"
					:value="size"
				>
					<div
						:class="[
							'cursor-pointer',
							'group relative flex items-center justify-center py-2.5 px-4 text-sm font-medium uppercase focus:outline-none flex-1 lg:px-0 lg:max-w-[78px] max-w-[54px]'
						]"
					>
						<RadioGroupLabel
							:id="`${size?.ParametricCode}-${size?.Id}`"
							class="z-10"
							as="span"
						>
							{{ size?.Code }}
						</RadioGroupLabel>
						<span
							:class="[
								checked
									? 'ring-[0.5px] border-transparent ring-primary'
									: 'border-quaternary border-solid',
								'shadow-sm border-[0.5px] pointer-events-none absolute -inset-px'
							]"
							aria-hidden="true"
						/>
						<span
							v-if="!size?.InStock"
							aria-hidden="true"
							class="pointer-events-none left-0 top-0 right-0 bottom-0 absolute border-[0.5px]"
						>
							<svg
								class="absolute inset-0 h-full w-full stroke-[0.5px]"
								viewBox="0 0 100 100"
								preserveAspectRatio="none"
								stroke="currentColor"
							>
								<line
									x1="0"
									y1="100"
									x2="100"
									y2="0"
									vector-effect="non-scaling-stroke"
								/>
							</svg>
						</span>
					</div>
				</RadioGroupOption>
			</div>
		</RadioGroup>
	</div>
</template>

<script setup lang="ts">
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import type {
	ParametricListValue,
	ParametricListValuewithCount
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { capitalize, computed } from "vue";

interface Props {
	parametricsSecondOne:
		| (ParametricListValue | ParametricListValuewithCount)[]
		| undefined;
}
const { parametricsSecondOne } = defineProps<Props>();

const { getAllSecondParametrics } = useNorcePwa();

const sizes = ref<(ParametricListValue | undefined)[]>();

const {
	selectedColor,
	products,
	parametricsInformation,
	selectedSecondParametric
} = storeToRefs(useProductStore());
const selectedSize = ref<ParametricListValue>();

const setInitialSecondParametric = ({
	parametricsSecondOne
}: {
	parametricsSecondOne:
		| (ParametricListValue | ParametricListValuewithCount)[]
		| undefined;
}) => {
	if (products.value) {
		const allSeccondParametrics = getAllSecondParametrics({
			parametrics: parametricsSecondOne,
			products: products.value,
			selectedColor: selectedColor.value as ParametricListValue
		});

		const parametricsSecondOneLocal = JSON.parse(
			JSON.stringify(allSeccondParametrics)
		) as (ParametricListValue | ParametricListValuewithCount)[];

		sizes.value = parametricsSecondOneLocal;
		selectedSize.value =
			sizes.value?.find(
				(size) => size?.Id === selectedSecondParametric.value?.Id
			) || sizes.value?.[0];
	}
};

watch(
	() => parametricsSecondOne,
	(newVal) => {
		setInitialSecondParametric({
			parametricsSecondOne: newVal
		});
	},
	{ immediate: true }
);

watch(selectedSize, () => {
	if (selectedSize.value) {
		useProductStore().addSelectedSecondParametric(selectedSize.value);
	}
});
watch(selectedColor, () => {
	if (selectedColor.value) {
		setInitialSecondParametric({
			parametricsSecondOne
		});
	}
});
const setName = computed(() => {
	return parametricsInformation.value.filter(
		(paramInfo) => paramInfo.Code === sizes?.value?.[0]?.ParametricCode
	)[0]?.Name;
});
</script>
